import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListProductsResponse,
  ProductDetail,
  ReviewQueueCountResponse,
  UpdateProductRequest,
  UpdateProductResponse,
  type ListProductsQuery,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export type ProductsParams = Partial<ListProductsQuery> & {
  /** Comma-separated status filter (overrides hidePassed if set). */
  status?: string;
  /** SPA defaults this to true on the list page. */
  hidePassed?: boolean;
  /** Phase 1.8 Pass 5 — filter to products created during a specific run. */
  searchRunId?: string;
};

export function productsQuery(params: ProductsParams = {}) {
  const search = new URLSearchParams();
  if (params.gradeMin) search.set('gradeMin', params.gradeMin);
  if (params.brand) search.set('brand', params.brand);
  if (params.category) search.set('category', params.category);
  if (params.status) search.set('status', params.status);
  if (params.hidePassed !== undefined) search.set('hidePassed', String(params.hidePassed));
  if (params.searchRunId) search.set('searchRunId', params.searchRunId);
  if (params.sortBy) search.set('sortBy', params.sortBy);
  if (params.sortOrder) search.set('sortOrder', params.sortOrder);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  const path = `/api/products${qs ? `?${qs}` : ''}`;

  return queryOptions({
    queryKey: [
      'products',
      params.gradeMin ?? '',
      params.brand ?? '',
      params.category ?? '',
      params.status ?? '',
      params.hidePassed ?? null,
      params.searchRunId ?? '',
      params.sortBy ?? 'roi',
      params.sortOrder ?? 'desc',
      params.limit ?? 50,
    ] as const,
    queryFn: () => apiFetch<ListProductsResponse>(path, { responseSchema: ListProductsResponse }),
    staleTime: 10_000,
  });
}

export function useProducts(params: ProductsParams = {}) {
  return useQuery(productsQuery(params));
}

export function productDetailQuery(asin: string) {
  return queryOptions({
    queryKey: ['products', 'detail', asin] as const,
    queryFn: () =>
      apiFetch<ProductDetail>(`/api/products/${asin}`, {
        responseSchema: ProductDetail,
      }),
    staleTime: 10_000,
    enabled: asin.length === 10,
  });
}

export function useProductDetail(asin: string) {
  return useQuery(productDetailQuery(asin));
}

/**
 * 24h-pending review queue count for the app-header chip — Phase 1.8 Pass 5.
 * Polled every 60s (refetchInterval) when mounted. Returns `0` for users
 * with no new pending products, which the chip uses to render hidden.
 */
export const reviewQueueCountQuery = queryOptions({
  queryKey: ['products', 'review-queue-count'] as const,
  queryFn: () =>
    apiFetch<ReviewQueueCountResponse>('/api/products/review-queue-count', {
      responseSchema: ReviewQueueCountResponse,
    }),
  staleTime: 30_000,
});

export function useReviewQueueCount() {
  return useQuery({
    ...reviewQueueCountQuery,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateProduct(asin: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProductRequest) =>
      apiFetch<UpdateProductResponse>(`/api/products/${asin}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: UpdateProductResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
